import React, { useState } from "react";
import {
  Stack,
  Typography,
  IconButton,
  Box,
  Fade,
  alpha,
  CardContent,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Markdown } from "src/components/markdown";
import { CleanMessageCard } from "./saanvi-styles";

function FilePreview({ file, onImageClick }) {
  const isImage =
    file.type?.startsWith("image/") ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);
  const isPDF =
    file.type === "application/pdf" ||
    file.name?.toLowerCase().endsWith(".pdf");

  if (isImage && file.preview) {
    return (
      <Box
        onClick={() => onImageClick(file.preview)}
        sx={{
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
          maxWidth: "300px",
          border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: (theme) =>
              `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      >
        <img
          src={file.preview}
          alt={file.name}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </Box>
    );
  }

  if (isPDF) {
    return (
      <Box
        sx={{
          borderRadius: "12px",
          padding: 2,
          background: (theme) => alpha(theme.palette.error.main, 0.05),
          border: (theme) =>
            `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
          maxWidth: "300px",
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PictureAsPdfIcon
            sx={{
              fontSize: 32,
              color: "error.main",
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: (theme) => alpha(theme.palette.text.secondary, 0.7),
              }}
            >
              PDF Document
            </Typography>
          </Box>
        </Stack>
      </Box>
    );
  }

  return null;
}

export default function MessageCard({
  message,
  index,
  onCopy,
  copied,
  onImageClick,
}) {
  const isUser = message.role === "user";

  return (
    <Fade in timeout={300}>
      <Box>
        {isUser ? (
          <Stack direction="row" justifyContent="flex-end">
            <CleanMessageCard isUser>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                {message.files && message.files.length > 0 && (
                  <Stack spacing={1} sx={{ mb: 1.5 }}>
                    {message.files.map((file, idx) => (
                      <FilePreview
                        key={idx}
                        file={file}
                        onImageClick={onImageClick}
                      />
                    ))}
                  </Stack>
                )}
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {message.content}
                </Typography>
              </CardContent>
            </CleanMessageCard>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <CleanMessageCard sx={{ flexGrow: 1 }}>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Markdown>{message.content}</Markdown>
              </CardContent>
            </CleanMessageCard>
            <IconButton size="small" onClick={onCopy} sx={{ mt: 0.5 }}>
              {copied ? (
                <CheckRoundedIcon
                  sx={{ fontSize: 16, color: "success.main" }}
                />
              ) : (
                <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Stack>
        )}
      </Box>
    </Fade>
  );
}
